import { useEffect, useRef } from "react";
import { type EmitterSubscription, DeviceEventEmitter } from "react-native";

/**
 * Type constraint for event maps - each event can have a payload type or void
 */
export type EventMap = Record<string, any>;

/**
 * Extract the payload type for a given event, handling void case
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type EventPayload<
  TEvents extends EventMap,
  TEventName extends keyof TEvents,
> = TEvents[TEventName] extends void ? void : TEvents[TEventName];

/**
 * Handler function type for a given event
 */
type EventHandler<
  TEvents extends EventMap,
  TEventName extends keyof TEvents,
> = TEvents[TEventName] extends void ? () => void : (payload: TEvents[TEventName]) => void;

/**
 * Modal Event Manager - provides type-safe event emission and listening for modals
 */
export interface ModalEventManager<TEvents extends EventMap> {
  /**
   * Emit an event with optional payload
   * @param eventType - The type of event to emit
   * @param payload - Optional payload data (required if event type expects data)
   */
  emit<TEventName extends keyof TEvents>(
    eventType: TEventName,
    ...args: TEvents[TEventName] extends void ? [] : [TEvents[TEventName]]
  ): void;

  /**
   * React hook to listen for a specific event type
   * @param eventType - The type of event to listen for
   * @param handler - Callback function to handle the event
   */
  useEvent<TEventName extends keyof TEvents>(
    eventType: TEventName,
    handler: EventHandler<TEvents, TEventName>,
  ): void;

  /**
   * Remove all listeners for this modal (useful for cleanup in tests)
   */
  removeAllListeners(): void;
}

/**
 * Create a type-safe event manager for a modal
 *
 * This factory function creates an event manager that handles communication
 * between modals (which may be in separate React roots) and their parent containers.
 *
 * @param modalName - Unique name for the modal (used as event namespace)
 * @returns Object with emit, useEvent, and removeAllListeners methods
 *
 * @example
 * ```typescript
 * type MyModalEvents = {
 *   saved: { userId: string };
 *   deleted: void;
 *   cancelled: void;
 * };
 *
 * const myModalEvents = createModalEventManager<MyModalEvents>('MyModal');
 *
 * // In modal - emit events
 * myModalEvents.emit('saved', { userId: '123' });
 * myModalEvents.emit('deleted'); // no payload for void events
 *
 * // In container - listen for events
 * myModalEvents.useEvent('saved', (data) => {
 *   console.log(data.userId); // TypeScript knows userId exists
 * });
 * ```
 */
export function createModalEventManager<TEvents extends EventMap>(
  modalName: string,
): ModalEventManager<TEvents> {
  // Track which event types have been used (for cleanup)
  const usedEventTypes = new Set<keyof TEvents>();

  const createEventName = (eventType: keyof TEvents): string => {
    return `${modalName}:${String(eventType)}`;
  };

  const emit = <TEventName extends keyof TEvents>(
    eventType: TEventName,
    ...args: TEvents[TEventName] extends void ? [] : [TEvents[TEventName]]
  ): void => {
    usedEventTypes.add(eventType);
    const eventName = createEventName(eventType);
    const payload = args[0];
    DeviceEventEmitter.emit(eventName, payload);
  };

  const useEvent = <TEventName extends keyof TEvents>(
    eventType: TEventName,
    handler: EventHandler<TEvents, TEventName>,
  ): void => {
    // Store the handler in a ref that updates every render
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
      usedEventTypes.add(eventType);
      const eventName = createEventName(eventType);

      const subscription: EmitterSubscription = DeviceEventEmitter.addListener(
        eventName,
        (payload: any) => {
          // Call the latest handler via ref

          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          (handlerRef.current as any)(payload);
        },
      );

      return () => {
        subscription.remove();
      };
    }, [eventType]); // Only eventType in deps - subscription stays stable
  };

  const removeAllListeners = (): void => {
    // Remove listeners for all event types that have been used
    // This is primarily useful for testing scenarios
    usedEventTypes.forEach((eventType) => {
      const eventName = createEventName(eventType);
      DeviceEventEmitter.removeAllListeners(eventName);
    });
  };

  return {
    emit,
    useEvent,
    removeAllListeners,
  };
}
